import DOMPurify, { type Config } from 'dompurify';

/**
 * Shared DOMPurify config for task descriptions.
 *
 * Allowed tags: prose + images (Odoo-hosted only).
 * img src is restricted to paths containing /web/image/ — any other src is
 * dropped by the afterSanitizeAttributes hook. This mirrors the server-side
 * sanitize-html exclusiveFilter so client and server agree on what is kept.
 */
export const DESC_ALLOWED_TAGS = [
  'p', 'br', 'div', 'span', 'b', 'i', 'strong', 'em', 'u',
  'ul', 'ol', 'li', 'a', 'img',
];

export const DESC_ALLOWED_ATTR = [
  'href', 'target', 'rel',          // a
  'src', 'alt', 'class', 'data-file-name', // img
];

export const DESC_CONFIG: Config = {
  ALLOWED_TAGS: DESC_ALLOWED_TAGS,
  ALLOWED_ATTR: DESC_ALLOWED_ATTR,
};

/**
 * Sanitize HTML with the shared task-description config.
 * img tags whose src does not contain /web/image/ are silently removed
 * (the hook modifies the node in place, and DOMPurify removes nodes whose
 * required attributes were blanked).
 *
 * Call this function — not DOMPurify.sanitize directly — so the hook is
 * always applied consistently.
 */
export function sanitizeDescription(html: string): string {
  // We must add the hook each time we call sanitize because DOMPurify hooks
  // are global. We remove it after the run to avoid leaking state into other
  // DOMPurify calls (e.g. SafeHtml).
  DOMPurify.addHook('afterSanitizeAttributes', enforceImgSrc);
  const result = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: DESC_ALLOWED_TAGS,
    ALLOWED_ATTR: DESC_ALLOWED_ATTR,
  });
  DOMPurify.removeHook('afterSanitizeAttributes');
  return result;
}

/**
 * An <img src> is only allowed if it's an absolute http(s) URL whose PATH begins
 * with /web/image/ (an Odoo-native attachment). Anchoring the scheme + authority
 * closes the substring bypasses (`data:...` /web/image/, `evil.com/x?a=/web/image/`,
 * protocol-relative `//evil`, userinfo `user@host`) that a bare `.includes` misses.
 *
 * Defense-in-depth only: the SERVER (`utils/description-html.ts`) is authoritative
 * for the stored value and pins the exact instance host. This client check can't
 * know the host without extra config, so a real external host at `/web/image/`
 * still passes here (harmless remote image, no script) — see TD.
 */
export function isAllowedImageSrc(src: string): boolean {
  return /^https?:\/\/[^/?#@]+\/web\/image\//i.test(src);
}

export function enforceImgSrc(node: Element): void {
  if (node.tagName !== 'IMG') return;
  const src = node.getAttribute('src') ?? '';
  if (!isAllowedImageSrc(src)) {
    node.parentNode?.removeChild(node);
  }
}

/**
 * Does a description have any renderable content? True if it has non-whitespace
 * text OR at least one <img> (a screenshot-only description is NOT empty).
 *
 * Use this instead of a raw `stripTags(...).length > 0` check anywhere a
 * description drives "has content" logic — otherwise an image-only description
 * strips to '' and is wrongly treated as empty (hidden from lists / read views).
 */
export function descriptionHasContent(html: string | false | null | undefined): boolean {
  if (!html) return false;
  if (/<img\b/i.test(html)) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
}
